import DataObject from "@mui/icons-material/DataObject";
import Laptop from "@mui/icons-material/Laptop";
import Search from "@mui/icons-material/Search";
import Storage from "@mui/icons-material/Storage";
import Tune from "@mui/icons-material/Tune";
import {
	Autocomplete,
	Box,
	Chip,
	createFilterOptions,
	InputAdornment,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

import {
	CATEGORY_BOOLEAN_VALUES,
	CATEGORY_DATABASE_TABLES,
	CATEGORY_SCREEN_FIELDS,
	CATEGORY_VARIABLES,
	type Option,
} from "../constants";
import { CanvasFormTextField } from "../forms/styled/CanvasFormTextField.styled";
import { MatchedValueChip } from "./MatchedValueChip";

export interface GroupedOption extends Option {
	category: string;
}

export interface AssignmentValueFieldProps {
	value: string;
	onChange: (value: string) => void;
	booleanValues?: Option[];
	variables?: Option[];
	screenFields?: Option[];
	databaseTables?: Option[];
	label?: string;
	fullWidth?: boolean;
	placeholder?: string;
}

const CATEGORY_ICONS: Record<string, React.ReactElement> = {
	[CATEGORY_BOOLEAN_VALUES]: <Tune fontSize="small" sx={{ color: "#D48200" }} />,
	[CATEGORY_VARIABLES]: <DataObject fontSize="small" sx={{ color: "#D48200" }} />,
	[CATEGORY_SCREEN_FIELDS]: <Laptop fontSize="small" sx={{ color: "#4CAF50" }} />,
	[CATEGORY_DATABASE_TABLES]: <Storage fontSize="small" sx={{ color: "#2196F3" }} />,
};

const filter = createFilterOptions<GroupedOption>();

const AssignmentValueField = (props: AssignmentValueFieldProps) => {
	const {
		value,
		onChange,
		booleanValues = [],
		variables = [],
		screenFields = [],
		databaseTables = [],
		label = "Value",
		fullWidth = true,
		placeholder = "Search or Enter Value Constant",
	} = props;

	const groupedOptions = useMemo<GroupedOption[]>(() => [
		...booleanValues.map((o) => ({ ...o, category: CATEGORY_BOOLEAN_VALUES })),
		...variables.map((o) => ({ ...o, category: CATEGORY_VARIABLES })),
		...screenFields.map((o) => ({ ...o, category: CATEGORY_SCREEN_FIELDS })),
		...databaseTables.map((o) => ({ ...o, category: CATEGORY_DATABASE_TABLES })),
	], [variables, screenFields, databaseTables]);

	const matchedOption = useMemo(
		() => groupedOptions.find((o) => o.value === value) ?? null,
		[groupedOptions, value],
	);

	const isMatched = matchedOption !== null;
	const [inputValue, setInputValue] = useState("");
	const [focused, setFocused] = useState(false);
	const justSelected = useRef(false);

	const showCustomChip = !isMatched && !!value && !focused;

	useEffect(() => {
		if (justSelected.current) {
			justSelected.current = false;
			return;
		}
		if (isMatched || showCustomChip) {
			setInputValue("");
		} else {
			setInputValue(value ?? "");
		}
	}, [value, isMatched, showCustomChip]);

	const handleChange = (_: unknown, newValue: GroupedOption | string | null) => {
		if (!newValue) {
			onChange("");
			setInputValue("");
		} else if (typeof newValue === "string") {
			onChange(newValue);
		} else {
			justSelected.current = true;
			onChange(newValue.value);
			setInputValue("");
		}
	};

	const handleInputChange = (_: unknown, newInput: string, reason: string) => {
		if (reason === "reset") return;
		if (reason === "input") {
			setInputValue(newInput);
			onChange(newInput);
		}
		if (reason === "clear") {
			onChange("");
			setInputValue("");
		}
	};

	const handleClearSelection = () => {
		onChange("");
		setInputValue("");
	};

	return (
		<Autocomplete<GroupedOption, false, false, true>
			freeSolo
			fullWidth={fullWidth}
			openOnFocus
			options={groupedOptions}
			groupBy={(option) => option.category}
			getOptionLabel={(option) =>
				typeof option === "string" ? option : option.label
			}
			filterOptions={(options, state) => {
				if (!state.inputValue) return options;
				return filter(options, state);
			}}
			value={matchedOption ?? value ?? ""}
			inputValue={inputValue}
			onChange={handleChange}
			onInputChange={handleInputChange}
			isOptionEqualToValue={(option, val) => {
				if (typeof option === "string" || typeof val === "string") return false;
				return option.value === val.value;
			}}
			clearOnBlur={false}
			selectOnFocus
			handleHomeEndKeys
			renderInput={({ slotProps: acSlotProps, ...params }) => {
				// MUI v9 moved the Autocomplete renderInput params under `slotProps`
				// ({ input, inputLabel, htmlInput }). The old top-level `InputProps` /
				// `inputProps` / `InputLabelProps` no longer exist, so reading
				// `params.InputProps.endAdornment` was reading `.endAdornment` off
				// undefined — which crashed the whole form (e.g. when adding a Query
				// Builder node, which renders this field).
				const htmlInputProps = acSlotProps.htmlInput;
				return (
				<CanvasFormTextField
					{...params}
					width="100%"
					label={label}
					placeholder={isMatched || showCustomChip ? undefined : placeholder}
					slotProps={{
						inputLabel: acSlotProps.inputLabel,
						htmlInput: {
							...htmlInputProps,
							onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
								setFocused(true);
								htmlInputProps?.onFocus?.(e);
							},
							onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
								setFocused(false);
								htmlInputProps?.onBlur?.(e);
							},
						},
						input: {
							...acSlotProps.input,
							startAdornment: (
								<>
									{isMatched ? (
										<MatchedValueChip
											size="small"
											category={matchedOption!.category}
											label={matchedOption!.label}
											onDelete={handleClearSelection}
											color="primary"
											variant="outlined"
											sx={{ maxWidth: 200 }}
											data-testid="selected-value-chip"
										/>
									) : showCustomChip ? (
										<Chip
											size="small"
											label={value}
											onDelete={handleClearSelection}
											sx={{ maxWidth: 200 }}
											data-testid="custom-value-chip"
										/>
									) : (
										<InputAdornment position="start">
											<Search fontSize="small" />
										</InputAdornment>
									)}
								</>
							),
							endAdornment: acSlotProps.input.endAdornment,
						},
					}}
				/>
				);
			}}
			renderOption={(props, option) => (
				<li {...props} key={option.value}>
					<Typography variant="body2">{option.label}</Typography>
				</li>
			)}
			renderGroup={(params) => (
				<li key={params.key}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							px: 2,
							py: 1,
							position: "sticky",
							top: -8,
							backgroundColor: "background.paper",
							zIndex: 1,
						}}
					>
						{CATEGORY_ICONS[params.group]}
						<Typography variant="caption" fontWeight={600} color="text.secondary">
							{params.group}
						</Typography>
					</Box>
					<ul style={{ padding: 0 }}>{params.children}</ul>
				</li>
			)}
		/>
	);
};

export default AssignmentValueField;
